import { useWorkspaceText } from '../../interface-copy';
import { useEffect, useRef, useState } from 'react';
import type { SavedItem } from '../../library/save-to-library';
import { openSavedApp } from './save-app';
import type { CanvasTarget } from '../../types';

export function PreviewBackendNotice({
  saved,
  send,
  openCanvas,
  openApps,
  scopeKey,
}: {
  saved: SavedItem | null;
  send: typeof fetch;
  openCanvas: (target: CanvasTarget) => void;
  openApps: () => void;
  /** Company/thread/frame identity. A late read must never navigate another scope. */
  scopeKey: string;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState(false);
  const alive = useRef(true);
  const identity = useRef(scopeKey);
  identity.current = scopeKey;
  useEffect(() => {
    alive.current = true;
    identity.current = scopeKey;
    return () => {
      alive.current = false;
    };
  }, [scopeKey]);
  const open = async () => {
    if (opening) return;
    setFailed(false);
    setOpening(true);
    try {
      const savedIdentity = saved && `${saved.artifactId}:${saved.versionId}:${saved.revisionId}`;
      if (saved) {
        let handedOff = false;
        await openSavedApp(saved, send, (target) => {
          const candidate = target as { id?: unknown; versionId?: unknown; revisionId?: unknown };
          if (
            alive.current &&
            identity.current === scopeKey &&
            `${candidate.id}:${candidate.versionId}:${candidate.revisionId}` === savedIdentity
          ) {
            handedOff = true;
            openCanvas(target);
          }
        });
        if (!handedOff) throw new Error('saved_target_changed');
      } else if (alive.current && identity.current === scopeKey) openApps();
    } catch {
      if (alive.current && identity.current === scopeKey) setFailed(true);
    } finally {
      if (alive.current && identity.current === scopeKey) setOpening(false);
    }
  };
  return (
    <div className="ap-preview-backend-notice" data-testid="preview-backend-notice" role="alert">
      <span>
        {saved
          ? uiText('Open the saved App to run this function.')
          : uiText('Save this App before running its function.')}
      </span>
      <button
        data-testid="preview-backend-open"
        type="button"
        onClick={() => void open()}
        disabled={opening}
      >
        {opening ? uiText('Opening…') : saved ? uiText('Open saved App') : uiText('Open Apps')}
      </button>
      {failed && <span role="status">{uiText('The App could not be opened. Try again.')}</span>}
    </div>
  );
}
